import { FC } from 'react';

import { Flex, ZZMForm, ZazumeModal } from '@zazume/zzm-base';
import { FormContextValues } from '@zazume/zzm-base/dist/components/form/context/FormContextProvider';
import { LanguageVO } from '@zazume/zzm-utils';

import { useNotifications } from '#/hooks';
import { useI18n } from '#/hooks/useI18n';
import { API } from '#/lib/network/API';
import { ErrorCode, useNetworkExceptions } from '#/lib/network/helpers';
import { User, isZazumeUser } from '#/models';
import { useLangOptions } from '#/utils/language';

import { FormInput, getSchema } from './UpdateProfile.schema';

interface EditProfileProps {
  user: User;
  close: () => void;
  onUpdate: () => void;
}

export const EditProfile: FC<EditProfileProps> = ({ user, onUpdate, close }) => {
  const { t, changeLanguage } = useI18n();
  const langOptions = useLangOptions(false);

  const { showNotification } = useNotifications();
  const { handleNetworkExceptions } = useNetworkExceptions();

  const defaultValues: FormInput = {
    name: user.name,
    surnames: user.surnames,
    phoneNumber: user.phoneNumber,
    email: user.email,
    avatar: user.avatar,
    bio: user.bio,
    videoUrl: user.videoUrl,
    lang: user.lang
  };

  const schema = getSchema(t);

  const updateProfile = async (formData: FormInput, context: Omit<FormContextValues, 'inlineErrors'>) => {
    const { avatar, ...updatedProfile } = formData;

    if (formData.lang !== user.lang) {
      changeLanguage(LanguageVO.from(formData.lang));
    }

    try {
      await API.users.update(updatedProfile);
      showNotification(t('profile.modal.updated'));
      onUpdate();
      close();
    } catch (error: any) {
      if (error.errorCode === ErrorCode.USER_ALREADY_EXISTS) {
        context.setError('email', { type: 'custom', message: t('general.forms.emailExisting') });
      } else {
        handleNetworkExceptions(error);
      }
    }
  };

  return (
    <ZZMForm.Form onSubmit={updateProfile} validation={schema} defaultValues={defaultValues}>
      <Flex direction="column" gap="xs">
        <ZZMForm.Input name="name" responsive={false} label={t('general.name')} />
        <ZZMForm.Input name="surnames" responsive={false} label={t('profile.surnames')} />
        <ZZMForm.PhoneInput name="phoneNumber" responsive={false} label={t('profile.phoneNumber')} />
        <ZZMForm.Input name="email" responsive={false} label={t('general.email')} />
        <ZZMForm.Select name="lang" label={t('general.language')} options={langOptions} />
        {isZazumeUser(user) && (
          <>
            <ZZMForm.Textarea name="bio" label={t('profile.bio')} />
            <ZZMForm.Input name="videoUrl" responsive={false} label={t('profile.videoUrl')} />
          </>
        )}
      </Flex>
      <ZZMForm.Debugger />
      <ZazumeModal.FormFooter acceptButtonText={t('profile.save')} cancelButtonText={t('buttons.cancel')} />
    </ZZMForm.Form>
  );
};
